import { toastService as toast } from "../components/ui/toast/toast.service";
import { WalletUpdateStatus } from "../store/app.store";
import { TransactionStatusTypes } from "../models/constants/transactionStatusTypes";
import localization from "../config/localization";
import { NotificationTypeConsts } from "../models/constants/notificationTypeConsts";
import { TransactionUpdate } from "../models/Response/TransactionUpdate";
import { useRealTimeStore } from "../store/signalrRealTime.store";
import { userPendingStore } from "../features/users/store/userPendingData.store";
import store from "../store/store";
import { increaseInboxUnreadCount } from "../features/users/reducers/userProfileReducer";
import { localStorageService } from "../services";

const signalREventNames = [
  NotificationTypeConsts.WithdrawUpdateStatus,
  NotificationTypeConsts.DepositUpdateStatus,
  NotificationTypeConsts.UserProofStatusUpdate,
  NotificationTypeConsts.UserBankStatusUpdate,
  NotificationTypeConsts.UserStatusUpdate,
  NotificationTypeConsts.SendInboxMessageByAdmin,
  NotificationTypeConsts.TransactionUpdate,
  NotificationTypeConsts.WithdrawRevert,
  NotificationTypeConsts.DepositRevert,
];

const dispatch = store.dispatch;

export const registerSignalREvents = (connection: any, userId: string) => {
  if (!connection) return;

  signalREventNames.forEach((eventName) => connection.off(eventName));

  const store = useRealTimeStore.getState();

  // ================= WITHDRAW UPDATE =================
  connection.on(NotificationTypeConsts.WithdrawUpdateStatus, (data: WalletUpdateStatus) => {
    const withdrawStatus = typeof data === "string" ? JSON.parse(data) : data;
    if (!withdrawStatus) return;

    let result = userPendingStore.getState().pendingData?.withdrawList;

    if (result?.some(x => x.id === withdrawStatus.id)) {

      store.setLatestWithdrawUpdate(withdrawStatus);

      if (withdrawStatus.status === TransactionStatusTypes.APPROVED) {
        if (withdrawStatus.utrNumber?.trim()) {
          const message = localization.withdraw_approved_utr_notification;
          toast.success(
            message
              .replace("amount", `₹${withdrawStatus.amount}`)
              .replace("utrnumber", withdrawStatus.utrNumber)
          );
        } else {
          const message = localization.withdraw_approved_notification;
          toast.success(
            message.replace("amount", `₹${withdrawStatus.amount}`)
          );
        }
        dispatch(increaseInboxUnreadCount());
      } else if (withdrawStatus.status === TransactionStatusTypes.REJECTED) {
        const message = localization.withdraw_rejected_notification;
        toast.error(
          message.replace("amount", `₹${withdrawStatus.amount}`)
        );
        dispatch(increaseInboxUnreadCount());
      }
    }
  });

  // ================= DEPOSIT UPDATE =================
  connection.on(NotificationTypeConsts.DepositUpdateStatus, (data: WalletUpdateStatus) => {
    const depositStatus = typeof data === "string" ? JSON.parse(data) : data;
    if (!depositStatus) return;

    let result = userPendingStore.getState().pendingData?.depositList;

    if (result && result.some(x => x.id === depositStatus.id)) {

      store.setLatestDepositUpdate(depositStatus);

      if (depositStatus.status === TransactionStatusTypes.APPROVED) {
        const message = localization.deposit_approved_notification;
        toast.success(
          message.replace("amount", `₹${depositStatus.amount}`)
        );
        dispatch(increaseInboxUnreadCount());
      } else if (depositStatus.status === TransactionStatusTypes.REJECTED) {
        const message = localization.deposit_rejected_notification;
        toast.error(
          message.replace("amount", `₹${depositStatus.amount}`)
        );
        dispatch(increaseInboxUnreadCount());
      }
    }
  });

  // ================= DOCUMENT STATUS =================
  connection.on(NotificationTypeConsts.UserProofStatusUpdate, (data: WalletUpdateStatus) => {

    const documentStatus = typeof data === "string" ? JSON.parse(data) : data;

    if (!documentStatus) return;
    if (documentStatus.userId?.toString() !== userId?.toString()) return;

    let result = userPendingStore.getState().pendingData?.proofList;

    if (result && result.some(x => x.id === documentStatus.id)) {

      store.setLatestDocumentUpdate(documentStatus);

      if (documentStatus.status === TransactionStatusTypes.APPROVED) {
        toast.success(localization.document_approved_message);
        dispatch(increaseInboxUnreadCount());
      } else if (documentStatus.status === TransactionStatusTypes.REJECTED) {
        toast.error(localization.document_rejected_message);
        dispatch(increaseInboxUnreadCount());
      }
    }
  });

  // ================= BANK STATUS =================
  connection.on(NotificationTypeConsts.UserBankStatusUpdate, (data: WalletUpdateStatus) => {
    const bankStatus = typeof data === "string" ? JSON.parse(data) : data;

    if (!bankStatus) return;

    let result = userPendingStore.getState().pendingData?.bankList;

    if (result && result.some(x => x.id === bankStatus.id)) {
      store.setLatestBankUpdate(bankStatus);

      if (bankStatus.status === TransactionStatusTypes.APPROVED) {

        toast.success(localization.bank_approved_messsage);
        dispatch(increaseInboxUnreadCount());
      } else if (bankStatus.status === TransactionStatusTypes.REJECTED) {

        toast.error(localization.bank_rejected_message);
        dispatch(increaseInboxUnreadCount());
      }
    }
  });

  // ================= FORCE LOGOUT =================
  connection.on(NotificationTypeConsts.UserStatusUpdate, (data: any) => {

    if (!data) return;
    if (data?.toString() !== userId?.toString()) return;

    localStorageService.clear();
    window.location.href = "/";
  });

  // ================= ADMIN MESSAGE =================
  connection.on(NotificationTypeConsts.SendInboxMessageByAdmin, (data: any) => {

    const userIds =
      typeof data === "string" ? JSON.parse(data) : data;

    if (!userIds?.includes(userId)) return;

    dispatch(increaseInboxUnreadCount());

  });

  // ================= TRANSACTION UPDATE =================
  connection.on(NotificationTypeConsts.TransactionUpdate, (data: TransactionUpdate) => {

    const transactionUpdate =
      typeof data === "string" ? JSON.parse(data) : data;

    if (!transactionUpdate) return;
    if (transactionUpdate.userId?.toString() !== userId?.toString()) return;

    store.addTransactionUpdate({
      id: transactionUpdate.transactionRequestUpdateId, // ✅ correct
      created: transactionUpdate.created,
      remarkstemplatecontent: transactionUpdate.englishMessageContent,
      remarkshindicontent: transactionUpdate.hindiMessageContent,
      isdepositupdate: transactionUpdate.isDepositUpdate,
    });
    dispatch(increaseInboxUnreadCount());
  });

  // ================= REVERT FLAGS =================
  connection.on(NotificationTypeConsts.WithdrawRevert, (data: any) => {

    const obj = typeof data === "string" ? JSON.parse(data) : data;
    if (!obj) return;

    store.setWithdrawRevert(obj.isOn === true);
  });

  connection.on(NotificationTypeConsts.DepositRevert, (data: any) => {
    const obj = typeof data === "string" ? JSON.parse(data) : data;
    if (!obj) return;

    store.setDepositRevert(obj.isOn === true);
  });
};

export const unregisterSignalREvents = (connection: any) => {
  if (!connection) return;
  signalREventNames.forEach((eventName) => connection.off(eventName));
};
